import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TypographyH2,
  TypographyMuted,
  TypographyPMedium,
  TypographyP,
  cn,
} from "@finster/ui";
import { ArrowLeft, Menu, Upload, X } from "lucide-react";
import {
  extractImagesFromPdf,
  extractPagesAsImages,
  extractVectorGraphics,
  ExtractedImage,
} from "../services/pdfService";
import { insertImagesAsSlides } from "../services/slideService";
import { usePdfThumbnails } from "../hooks/usePdfThumbnails";

interface PdfUploaderProps {
  onLog?: (message: string) => void;
  extractedImages: ExtractedImage[];
  setExtractedImages: (images: ExtractedImage[]) => void;
  selectedImages: Set<number>;
  setSelectedImages: (selected: Set<number>) => void;
  onOpenSidebar: () => void;
}

type ExtractMode = "images" | "vectors" | "pages";

export const PdfUploader: React.FC<PdfUploaderProps> = ({
  onLog,
  extractedImages,
  setExtractedImages,
  selectedImages,
  setSelectedImages,
  onOpenSidebar,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractMode, setExtractMode] = useState<ExtractMode>("images");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const operationGenerationRef = useRef(0);
  const operationAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
      operationAbortRef.current?.abort();
      operationAbortRef.current = null;
    };
  }, []);
  const thumbnails = usePdfThumbnails(selectedFiles);
  const areThumbnailsReady = selectedFiles.length > 0 && thumbnails.length === selectedFiles.length;

  const log = (msg: string) => {
    if (mountedRef.current) onLog?.(msg);
  };

  const normalizePdfFiles = (files: FileList | File[]): File[] =>
    Array.from(files).filter(
      (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
    );

  const setFilesFromInput = (files: FileList | File[]) => {
    const pdfFiles = normalizePdfFiles(files);
    if (pdfFiles.length === 0) {
      setError("Please select at least one PDF file");
      return;
    }

    setError(null);
    setSelectedFiles(pdfFiles);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setFilesFromInput(files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isLoading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isLoading) return;

    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;
    setFilesFromInput(files);
  };

  const removeSelectedFile = (indexToRemove: number) => {
    if (isLoading) return;

    setSelectedFiles((previous) => previous.filter((_, index) => index !== indexToRemove));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const extractFromSinglePdf = async (
    file: File,
    signal: AbortSignal
  ): Promise<ExtractedImage[]> => {
    let allImages: ExtractedImage[] = [];

    if (extractMode === "pages") {
      const result = await extractPagesAsImages(file, signal);
      if (result.success) allImages = result.images;
      return allImages;
    }

    if (extractMode === "vectors") {
      const result = await extractVectorGraphics(file, signal);
      if (result.success) allImages = result.images;
      return allImages;
    }

    const imagesResult = await extractImagesFromPdf(file, signal);
    if (imagesResult.success && imagesResult.images.length > 0) {
      allImages = [...imagesResult.images];
      log(`Found ${imagesResult.images.length} embedded images in ${file.name}`);
    }

    const vectorsResult = await extractVectorGraphics(file, signal);
    if (vectorsResult.success && vectorsResult.images.length > 0) {
      allImages = [...allImages, ...vectorsResult.images];
      log(`Found ${vectorsResult.images.length} vector graphics in ${file.name}`);
    }

    return allImages;
  };

  const handleUploadAndExtract = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one PDF file");
      return;
    }

    const operationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = operationGeneration;
    operationAbortRef.current?.abort();
    const operationAbort = new AbortController();
    operationAbortRef.current = operationAbort;
    const operationIsCurrent = () =>
      mountedRef.current &&
      !operationAbort.signal.aborted &&
      operationGenerationRef.current === operationGeneration;
    setIsLoading(true);
    setError(null);
    setExtractedImages([]);
    setSelectedImages(new Set());

    try {
      const allImages: ExtractedImage[] = [];
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        log(`Uploading PDF ${index + 1}/${selectedFiles.length}: ${file.name}`);
        const fileImages = await extractFromSinglePdf(file, operationAbort.signal);
        if (!operationIsCurrent()) return;
        allImages.push(...fileImages);
      }

      if (!operationIsCurrent()) return;
      if (allImages.length === 0) {
        if (extractMode === "images") {
          setError("No charts, vectors, or embedded images found. Try 'Import PDF as an image'.");
        } else if (extractMode === "vectors") {
          setError("No charts or vector graphics found. Try 'Import PDF as an image'.");
        } else {
          setError("Could not render PDF pages.");
        }
        return;
      }

      setExtractedImages(allImages);
      setSelectedImages(new Set(allImages.map((_, i) => i)));
      log(`Found ${allImages.length} total graphics across ${selectedFiles.length} PDF(s)`);
    } catch (err) {
      if (!operationIsCurrent()) return;
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(errorMsg);
      log(`Error: ${errorMsg}`);
    } finally {
      if (operationIsCurrent()) setIsLoading(false);
      if (operationAbortRef.current === operationAbort) {
        operationAbortRef.current = null;
      }
    }
  };

  const toggleImageSelection = (index: number) => {
    const next = new Set(selectedImages);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelectedImages(next);
  };

  const allSelected = selectedImages.size === extractedImages.length;
  const toggleSelectAll = () => {
    if (allSelected) setSelectedImages(new Set());
    else setSelectedImages(new Set(extractedImages.map((_, i) => i)));
  };

  const handleInsertSlides = async () => {
    const imagesToInsert = extractedImages.filter((_, i) => selectedImages.has(i));
    if (imagesToInsert.length === 0) {
      setError("Please select at least one image");
      return;
    }

    const operationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = operationGeneration;
    operationAbortRef.current?.abort();
    const operationAbort = new AbortController();
    operationAbortRef.current = operationAbort;
    const operationIsCurrent = () =>
      mountedRef.current &&
      !operationAbort.signal.aborted &&
      operationGenerationRef.current === operationGeneration;
    setIsLoading(true);

    try {
      log(`Inserting ${imagesToInsert.length} images as slides...`);
      await insertImagesAsSlides(imagesToInsert, log, operationAbort.signal);
      if (!operationIsCurrent()) return;

      setExtractedImages([]);
      setSelectedImages(new Set());
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      if (!operationIsCurrent()) return;
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(errorMsg);
      log(`Error: ${errorMsg}`);
    } finally {
      if (operationIsCurrent()) setIsLoading(false);
      if (operationAbortRef.current === operationAbort) {
        operationAbortRef.current = null;
      }
    }
  };

  const handleBackToUpload = () => {
    setExtractedImages([]);
    setSelectedImages(new Set());
    setError(null);
  };

  const showResults = extractedImages.length > 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex-none px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {showResults ? (
            <button
              type="button"
              onClick={handleBackToUpload}
              aria-label="Back to upload"
              className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              onClick={onOpenSidebar}
              aria-label="Open sidebar"
              className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
          )}
          <TypographyP>PDF Extractor</TypographyP>
        </div>
      </div>

      {showResults ? (
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-3">
          <div className="flex-none flex items-center justify-between pb-3">
            <span className="text-sm font-medium text-foreground">
              {selectedImages.size} of {extractedImages.length} selected
            </span>
            <Button type="button" variant="outline" size="sm" onClick={toggleSelectAll}>
              {allSelected ? "Deselect All" : "Select All"}
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-background-secondary p-2">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(85px,1fr))] gap-2">
              {extractedImages.map((img, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => toggleImageSelection(index)}
                  className={cn(
                    "group relative overflow-hidden rounded-md border bg-background text-left transition",
                    selectedImages.has(index)
                      ? "border-primary ring-2 ring-primary/60"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <img
                    src={`data:image/${img.format};base64,${img.base64_data}`}
                    alt={`Page ${img.page_number}`}
                    className={cn(
                      "h-[65px] w-full object-cover transition-opacity",
                      selectedImages.has(index)
                        ? "opacity-100"
                        : "opacity-70 group-hover:opacity-90"
                    )}
                  />
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold text-white">
                    {img.page_number}
                  </span>
                  {selectedImages.has(index) && (
                    <span className="absolute top-1 right-1 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-none pt-3">
            <Button
              disabled={isLoading || selectedImages.size === 0}
              onClick={handleInsertSlides}
              shape="round"
              className="w-full font-semibold text-[13px]"
            >
              Add {selectedImages.size} image{selectedImages.size !== 1 ? "s" : ""} to presentation
            </Button>
            {error && (
              <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        </main>
      ) : (
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-1 pb-3">
          <section className="mb-4 pt-2">
            <TypographyH2 className="border-0 pb-0">Import from PDFs</TypographyH2>
            <TypographyMuted className="mt-1">
              Extract graphics or import PDFs as new editable slides
            </TypographyMuted>
          </section>

          <section className="mb-4">
            <Select
              value={extractMode}
              onValueChange={(value) => setExtractMode(value as ExtractMode)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-11 w-full rounded-lg bg-background text-sm">
                <SelectValue>
                  {extractMode === "images" && "Import charts and vectors"}
                  {extractMode === "vectors" && "Import only charts"}
                  {extractMode === "pages" && "Import PDF as an image"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                <SelectItem value="images" className="cursor-pointer pl-3 pr-3 data-[highlighted]:bg-accent data-[state=checked]:text-primary data-[state=checked]:font-semibold [&>span:first-child]:hidden">
                  Import charts and vectors (Recommended)
                </SelectItem>
                <SelectItem value="vectors" className="cursor-pointer pl-3 pr-3 data-[highlighted]:bg-accent data-[state=checked]:text-primary data-[state=checked]:font-semibold [&>span:first-child]:hidden">
                  Import only charts
                </SelectItem>
                <SelectItem value="pages" className="cursor-pointer pl-3 pr-3 data-[highlighted]:bg-accent data-[state=checked]:text-primary data-[state=checked]:font-semibold [&>span:first-child]:hidden">
                  Import PDF as an image
                </SelectItem>
              </SelectContent>
            </Select>
          </section>

          {areThumbnailsReady && (
            <section className="mb-4">
              <div className="flex gap-2 overflow-x-auto pt-2 pb-1 pr-1">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${file.lastModified}-${index}`}
                    className="relative h-[60px] w-[48px] flex-none"
                  >
                    <div className="h-full w-full overflow-hidden rounded-md border border-border bg-background-secondary">
                      {thumbnails[index] ? (
                        <img
                          src={thumbnails[index]}
                          alt={`Thumbnail of ${file.name}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] font-medium text-muted-foreground">
                          PDF
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeSelectedFile(index)}
                      className="absolute -top-1.5 -right-1.5 z-[1] inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section
            className={cn(
              "mb-4 rounded-lg border border-dashed border-border bg-background p-6 text-center transition-colors",
              isDragging && "border-primary bg-primary/5",
              isLoading && "opacity-70"
            )}
            onClick={() => !isLoading && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleInputChange}
              disabled={isLoading}
              className="hidden"
            />
            <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center text-muted-foreground">
              <Upload className="h-6 w-6" />
            </div>
            <TypographyPMedium className="leading-tight text-foreground">
              Drop multiple PDFs or{" "}
              <span className="underline decoration-2 underline-offset-2">Browse</span>
            </TypographyPMedium>
          </section>

          <Button
            variant="default"
            onClick={handleUploadAndExtract}
            disabled={isLoading || selectedFiles.length === 0 || !areThumbnailsReady}
            shape="round"
            className="w-full disabled:opacity-50"
          >
            {isLoading
            ? "Extracting..."
            : selectedFiles.length > 0 && !areThumbnailsReady
              ? "Uploading PDF..."
              : areThumbnailsReady
                ? "Extract from PDF"
                : "Upload PDF"}
          </Button>

          {error && (
            <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default PdfUploader;
